import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaArchive, FaCapsules, FaEdit, FaEye, FaSearch, FaTags, FaUndo } from "react-icons/fa";
import ManagementHeader from "../../../components/ManagementHeader";
import Footer from "../../../components/Footer";
import DrugCategoryGuide from "../../../components/DrugCategoryGuide";
import { supabase } from "../../../supabaseClient";
import { loadDrugMatrix, slugifyDrug } from "../../../lib/drugMatrix";
import { formatProfileName } from "../../../utils/profileName";

const emptyDrug = {
  id: null,
  name: "",
  slug: "",
  alternative_name: "",
  category_id: "",
  description: "",
  indications: "",
  dosage_notes: "",
  contraindications: "",
  warnings: "",
  additional_notes: "",
  sort_order: 100,
  archived: false,
  label_ids: [],
};

const emptyLabel = { id: null, name: "", description: "", active: true };

export default function ManagementDrugMatrix({ session, profile }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("drugs");
  const [matrix, setMatrix] = useState({ categories: [], labels: [], drugs: [] });
  const [audit, setAudit] = useState([]);
  const [actorNames, setActorNames] = useState({});
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [archiveFilter, setArchiveFilter] = useState("active");
  const [drugForm, setDrugForm] = useState(emptyDrug);
  const [labelForm, setLabelForm] = useState(emptyLabel);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [loading, setLoading] = useState(true);
  const drugEditorRef = useRef(null);
  const drugNameRef = useRef(null);
  const labelEditorRef = useRef(null);
  const labelNameRef = useRef(null);

  async function load() {
    setLoading(true);
    try {
      const result = await loadDrugMatrix({ includeArchived: true });
      setMatrix(result);
      if (!drugForm.category_id && result.categories[0]) {
        setDrugForm((current) => ({ ...current, category_id: String(result.categories[0].id) }));
      }
      const { data: auditRows, error: auditError } = await supabase
        .from("drug_matrix_audit")
        .select("*")
        .order("changed_at", { ascending: false })
        .limit(100);
      if (auditError) throw auditError;
      const rows = auditRows || [];
      setAudit(rows);
      const ids = [...new Set(rows.map((row) => row.changed_by).filter(Boolean))];
      if (ids.length) {
        const { data: actors } = await supabase.from("profiles").select("id,full_name,name_prefix,roblox_name").in("id", ids);
        setActorNames(Object.fromEntries((actors || []).map((actor) => [actor.id, formatProfileName(actor)])));
      } else setActorNames({});
    } catch (error) {
      setFeedback(error.message || "Could not load the Drug Matrix administration data.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  const visibleDrugs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return matrix.drugs.filter((drug) => {
      if (archiveFilter === "active" && drug.archived) return false;
      if (archiveFilter === "archived" && !drug.archived) return false;
      if (categoryFilter !== "all" && String(drug.category_id) !== categoryFilter) return false;
      if (!q) return true;
      return [drug.name, drug.alternative_name, drug.category?.name, ...drug.labels.map((label) => label.name)]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q));
    });
  }, [matrix.drugs, search, archiveFilter, categoryFilter]);

  function focusEditor(editorRef, inputRef) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        editorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        window.setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 350);
      });
    });
  }

  function editDrug(drug) {
    setTab("drugs");
    setFeedback("");
    setDrugForm({
      ...emptyDrug,
      ...drug,
      category_id: String(drug.category_id),
      label_ids: drug.labels.map((label) => label.id),
    });
    focusEditor(drugEditorRef, drugNameRef);
  }

  function cancelDrugEdit() {
    setDrugForm({ ...emptyDrug, category_id: matrix.categories[0] ? String(matrix.categories[0].id) : "" });
    setFeedback("");
    focusEditor(drugEditorRef, drugNameRef);
  }

  async function saveDrug(event) {
    event.preventDefault();
    setBusy(true);
    setFeedback("");
    try {
      if (!drugForm.category_id) throw new Error("Choose a predefined category.");
      const payload = {
        name: drugForm.name.trim(),
        slug: drugForm.slug.trim() || slugifyDrug(drugForm.name),
        alternative_name: drugForm.alternative_name.trim(),
        category_id: Number(drugForm.category_id),
        description: drugForm.description.trim(),
        indications: drugForm.indications.trim(),
        dosage_notes: drugForm.dosage_notes.trim(),
        contraindications: drugForm.contraindications.trim(),
        warnings: drugForm.warnings.trim(),
        additional_notes: drugForm.additional_notes.trim(),
        sort_order: Number(drugForm.sort_order) || 100,
        archived: Boolean(drugForm.archived),
        updated_by: session?.user?.id || null,
        updated_at: new Date().toISOString(),
      };

      let drugId = drugForm.id;
      if (drugForm.id) {
        const { error } = await supabase.from("drug_matrix_drugs").update(payload).eq("id", drugForm.id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase.from("drug_matrix_drugs").insert({ ...payload, created_by: session?.user?.id || null }).select("id").single();
        if (error) throw error;
        drugId = data.id;
      }

      const previousLabelIds = drugForm.id
        ? (matrix.drugs.find((item) => item.id === drugForm.id)?.labels || []).map((item) => item.id)
        : [];
      const removedLabelIds = previousLabelIds.filter((labelId) => !drugForm.label_ids.includes(labelId));
      const addedLabelIds = drugForm.label_ids.filter((labelId) => !previousLabelIds.includes(labelId));

      if (removedLabelIds.length) {
        const { error: removeLabelError } = await supabase
          .from("drug_matrix_drug_labels")
          .delete()
          .eq("drug_id", drugId)
          .in("label_id", removedLabelIds);
        if (removeLabelError) throw removeLabelError;
      }
      if (addedLabelIds.length) {
        const { error: labelError } = await supabase.from("drug_matrix_drug_labels").insert(addedLabelIds.map((labelId) => ({ drug_id: drugId, label_id: labelId, assigned_by: session?.user?.id || null })));
        if (labelError) throw labelError;
      }

      setFeedback(drugForm.id ? "Drug Matrix entry updated." : "Drug Matrix entry created.");
      setDrugForm({ ...emptyDrug, category_id: matrix.categories[0] ? String(matrix.categories[0].id) : "" });
      await load();
    } catch (error) {
      setFeedback(error.message || "Could not save the Drug Matrix entry.");
    } finally {
      setBusy(false);
    }
  }

  async function setArchived(drug, archived) {
    const verb = archived ? "archive" : "restore";
    if (!window.confirm(`${verb === "archive" ? "Archive" : "Restore"} ${drug.name}?`)) return;
    const { error } = await supabase.from("drug_matrix_drugs").update({ archived, updated_by: session?.user?.id || null, updated_at: new Date().toISOString() }).eq("id", drug.id);
    setFeedback(error ? error.message : `${drug.name} ${archived ? "archived" : "restored"}.`);
    if (!error) await load();
  }

  function editLabel(label) {
    setTab("labels");
    setFeedback("");
    setLabelForm({ id: label.id, name: label.name, description: label.description || "", active: label.active });
    focusEditor(labelEditorRef, labelNameRef);
  }

  function cancelLabelEdit() {
    setLabelForm(emptyLabel);
    setFeedback("");
    focusEditor(labelEditorRef, labelNameRef);
  }

  async function saveLabel(event) {
    event.preventDefault();
    setBusy(true);
    setFeedback("");
    const payload = {
      name: labelForm.name.trim(),
      description: labelForm.description.trim(),
      active: Boolean(labelForm.active),
      updated_by: session?.user?.id || null,
      updated_at: new Date().toISOString(),
    };
    let result;
    if (labelForm.id) result = await supabase.from("drug_matrix_labels").update(payload).eq("id", labelForm.id);
    else result = await supabase.from("drug_matrix_labels").insert({ ...payload, created_by: session?.user?.id || null });
    setBusy(false);
    if (result.error) return setFeedback(result.error.message);
    setFeedback(labelForm.id ? "Administration label updated." : "Administration label created.");
    setLabelForm(emptyLabel);
    await load();
  }

  const activeCount = matrix.drugs.filter((drug) => !drug.archived).length;
  const archivedCount = matrix.drugs.filter((drug) => drug.archived).length;

  return <>
    <ManagementHeader session={session} profile={profile} />
    <main className="nhsuk-main-wrapper pguh-drug-admin" id="main-content" tabIndex="-1">
      <div className="nhsuk-width-container pguh-drug-page-shell">
        <header className="pguh-drug-admin-hero">
          <div className="pguh-drug-hero-copy">
            <span className="pguh-eyebrow">Management</span>
            <h1><FaCapsules /> Drug Matrix administration</h1>
            <p>Maintain the searchable staff Drug Matrix, predefined medicine categories and administration labels.</p>
          </div>
          <button type="button" className="nhsuk-button nhsuk-button--secondary pguh-drug-hero-action" onClick={() => navigate("/intranet/drug-matrix")}><FaEye /> Open staff matrix</button>
        </header>

        {feedback && <div className="nhsuk-inset-text pguh-drug-feedback">{feedback}</div>}

        <div className="pguh-drug-admin-stats">
          <div><strong>{activeCount}</strong><span>Active medicines</span></div>
          <div><strong>{archivedCount}</strong><span>Archived medicines</span></div>
          <div><strong>{matrix.categories.length}</strong><span>Predefined categories</span></div>
          <div><strong>{matrix.labels.filter((label) => label.active).length}</strong><span>Active labels</span></div>
        </div>

        <DrugCategoryGuide compact />

        <nav className="pguh-drug-admin-tabs" aria-label="Drug Matrix administration sections">
          <button type="button" className={tab === "drugs" ? "is-active" : ""} onClick={() => setTab("drugs")}><FaCapsules /> <span>Drugs</span></button>
          <button type="button" className={tab === "labels" ? "is-active" : ""} onClick={() => setTab("labels")}><FaTags /> <span>Administration labels</span></button>
          <button type="button" className={tab === "audit" ? "is-active" : ""} onClick={() => setTab("audit")}><span>Audit history</span></button>
        </nav>

        {tab === "drugs" && <div className="pguh-drug-admin-layout">
          <section ref={drugEditorRef} className={`pguh-drug-admin-panel pguh-drug-admin-editor ${drugForm.id ? "is-editing" : ""}`}>
            <div className="pguh-drug-admin-panel__heading">
              <div><span className="pguh-eyebrow">Editor</span><h2>{drugForm.id ? `Edit ${drugForm.name || "medicine"}` : "Add medicine"}</h2></div>
              {drugForm.id && <button className="nhsuk-button nhsuk-button--secondary pguh-drug-small-button" type="button" onClick={cancelDrugEdit}>Cancel editing</button>}
            </div>

            {drugForm.id && <div className="pguh-drug-edit-notice" role="status"><strong>Editing existing medicine</strong><span>Update any field below, then select <b>Save changes</b>.</span></div>}

            <form className="pguh-drug-form" onSubmit={saveDrug}>
              <div className="pguh-drug-form-grid">
                <label className="pguh-drug-field">
                  <span className="pguh-drug-field__label">Drug name</span>
                  <input ref={drugNameRef} className="nhsuk-input" required value={drugForm.name} onChange={(event) => setDrugForm((form) => ({ ...form, name: event.target.value, slug: form.id ? form.slug : slugifyDrug(event.target.value) }))} />
                </label>
                <label className="pguh-drug-field">
                  <span className="pguh-drug-field__label">Alternative / generic name</span>
                  <input className="nhsuk-input" value={drugForm.alternative_name} onChange={(event) => setDrugForm((form) => ({ ...form, alternative_name: event.target.value }))} />
                </label>
              </div>

              <div className="pguh-drug-form-grid">
                <label className="pguh-drug-field">
                  <span className="pguh-drug-field__label">Category</span>
                  <select className="nhsuk-select" required value={drugForm.category_id} onChange={(event) => setDrugForm((form) => ({ ...form, category_id: event.target.value }))}>
                    <option value="">Choose category</option>
                    {matrix.categories.map((item) => <option key={item.id} value={String(item.id)}>{item.name}</option>)}
                  </select>
                  <small>Categories are predefined and cannot be created here.</small>
                </label>
                <label className="pguh-drug-field">
                  <span className="pguh-drug-field__label">URL slug</span>
                  <input className="nhsuk-input" required value={drugForm.slug} onChange={(event) => setDrugForm((form) => ({ ...form, slug: slugifyDrug(event.target.value) }))} />
                  <small>/intranet/drug-matrix/{drugForm.slug || "medicine-name"}</small>
                </label>
              </div>

              <label className="pguh-drug-field pguh-drug-field--textarea">
                <span className="pguh-drug-field__label">Description / summary</span>
                <textarea className="nhsuk-textarea" rows="4" value={drugForm.description} onChange={(event) => setDrugForm((form) => ({ ...form, description: event.target.value }))} />
              </label>

              <label className="pguh-drug-field pguh-drug-field--textarea">
                <span className="pguh-drug-field__label">Indications</span>
                <textarea className="nhsuk-textarea" rows="4" value={drugForm.indications} onChange={(event) => setDrugForm((form) => ({ ...form, indications: event.target.value }))} />
              </label>

              <label className="pguh-drug-field pguh-drug-field--textarea">
                <span className="pguh-drug-field__label">Dosage / administration notes</span>
                <textarea className="nhsuk-textarea" rows="5" value={drugForm.dosage_notes} onChange={(event) => setDrugForm((form) => ({ ...form, dosage_notes: event.target.value }))} />
              </label>

              <div className="pguh-drug-form-grid pguh-drug-form-grid--textareas">
                <label className="pguh-drug-field pguh-drug-field--textarea">
                  <span className="pguh-drug-field__label">Contraindications</span>
                  <textarea className="nhsuk-textarea" rows="4" value={drugForm.contraindications} onChange={(event) => setDrugForm((form) => ({ ...form, contraindications: event.target.value }))} />
                </label>
                <label className="pguh-drug-field pguh-drug-field--textarea">
                  <span className="pguh-drug-field__label">Warnings / cautions</span>
                  <textarea className="nhsuk-textarea" rows="4" value={drugForm.warnings} onChange={(event) => setDrugForm((form) => ({ ...form, warnings: event.target.value }))} />
                </label>
              </div>

              <label className="pguh-drug-field pguh-drug-field--textarea">
                <span className="pguh-drug-field__label">Additional notes</span>
                <textarea className="nhsuk-textarea" rows="4" value={drugForm.additional_notes} onChange={(event) => setDrugForm((form) => ({ ...form, additional_notes: event.target.value }))} />
              </label>

              <fieldset className="pguh-drug-label-picker">
                <legend>Who can administer / labels</legend>
                {matrix.labels.filter((item) => item.active || drugForm.label_ids.includes(item.id)).length === 0 ? <p>No labels exist yet. Create them in the Administration labels tab.</p> : <div className="pguh-drug-label-picker__grid">
                  {matrix.labels.filter((item) => item.active || drugForm.label_ids.includes(item.id)).map((item) => <label key={item.id} className="pguh-drug-label-option">
                    <input type="checkbox" checked={drugForm.label_ids.includes(item.id)} onChange={(event) => setDrugForm((form) => ({ ...form, label_ids: event.target.checked ? [...form.label_ids, item.id] : form.label_ids.filter((id) => id !== item.id) }))} />
                    <span><strong>{item.name}</strong>{item.description && <small>{item.description}</small>}</span>
                  </label>)}
                </div>}
              </fieldset>

              <div className="pguh-drug-form-footer">
                <label className="pguh-drug-field pguh-drug-field--sort">
                  <span className="pguh-drug-field__label">Sort order</span>
                  <input className="nhsuk-input" type="number" value={drugForm.sort_order} onChange={(event) => setDrugForm((form) => ({ ...form, sort_order: event.target.value }))} />
                </label>
                {drugForm.id && <label className="pguh-drug-inline-check"><input type="checkbox" checked={drugForm.archived} onChange={(event) => setDrugForm((form) => ({ ...form, archived: event.target.checked }))} /><span>Archived</span></label>}
                <button className="nhsuk-button pguh-drug-primary-action" type="submit" disabled={busy}>{busy ? "Saving…" : drugForm.id ? "Save changes" : "Add medicine"}</button>
              </div>
            </form>
          </section>

          <section className="pguh-drug-admin-panel pguh-drug-admin-register">
            <div className="pguh-drug-admin-panel__heading"><div><span className="pguh-eyebrow">Register</span><h2>Medicines</h2></div><strong>{visibleDrugs.length}</strong></div>
            <div className="pguh-drug-admin-filters">
              <label className="pguh-drug-field"><span className="pguh-drug-field__label"><FaSearch /> Search</span><input className="nhsuk-input" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Drug name or label" /></label>
              <label className="pguh-drug-field"><span className="pguh-drug-field__label">Category</span><select className="nhsuk-select" value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)}><option value="all">All categories</option>{matrix.categories.map((item) => <option key={item.id} value={String(item.id)}>{item.name}</option>)}</select></label>
              <label className="pguh-drug-field"><span className="pguh-drug-field__label">Status</span><select className="nhsuk-select" value={archiveFilter} onChange={(event) => setArchiveFilter(event.target.value)}><option value="active">Active</option><option value="archived">Archived</option><option value="all">All</option></select></label>
            </div>

            {loading ? <p>Loading medicines…</p> : <div className="pguh-drug-admin-list">
              {visibleDrugs.map((drug) => <article key={drug.id} className={drug.archived ? "is-archived" : ""}>
                <div className="pguh-drug-admin-list__content">
                  <div className="pguh-drug-card__category">{drug.category?.name}</div>
                  <h3>{drug.name}</h3>
                  {drug.alternative_name && <p>{drug.alternative_name}</p>}
                  <div className="pguh-drug-labels">{drug.labels.map((label) => <span key={label.id}>{label.name}</span>)}</div>
                </div>
                <div className="pguh-drug-admin-list__actions">
                  <button className="nhsuk-button nhsuk-button--secondary" type="button" onClick={() => editDrug(drug)}><FaEdit /> Edit</button>
                  <button className="nhsuk-button nhsuk-button--secondary" type="button" onClick={() => navigate(`/intranet/drug-matrix/${drug.slug}`)} disabled={drug.archived}><FaEye /> Preview</button>
                  {drug.archived ? <button className="nhsuk-button" type="button" onClick={() => setArchived(drug, false)}><FaUndo /> Restore</button> : <button className="nhsuk-button nhsuk-button--warning" type="button" onClick={() => setArchived(drug, true)}><FaArchive /> Archive</button>}
                </div>
              </article>)}
            </div>}
          </section>
        </div>}

        {tab === "labels" && <div className="pguh-drug-admin-layout pguh-drug-admin-layout--labels">
          <section ref={labelEditorRef} className={`pguh-drug-admin-panel pguh-drug-admin-editor ${labelForm.id ? "is-editing" : ""}`}>
            <div className="pguh-drug-admin-panel__heading">
              <div><span className="pguh-eyebrow">Reusable label</span><h2>{labelForm.id ? `Edit ${labelForm.name || "label"}` : "Create label"}</h2></div>
              {labelForm.id && <button className="nhsuk-button nhsuk-button--secondary pguh-drug-small-button" type="button" onClick={cancelLabelEdit}>Cancel editing</button>}
            </div>
            {labelForm.id && <div className="pguh-drug-edit-notice" role="status"><strong>Editing existing label</strong><span>Change the label details below, then select <b>Save changes</b>.</span></div>}
            <form className="pguh-drug-form" onSubmit={saveLabel}>
              <label className="pguh-drug-field"><span className="pguh-drug-field__label">Label name</span><input ref={labelNameRef} className="nhsuk-input" required value={labelForm.name} onChange={(event) => setLabelForm((form) => ({ ...form, name: event.target.value }))} placeholder="e.g. Doctor, Nurse, Paramedic" /></label>
              <label className="pguh-drug-field pguh-drug-field--textarea"><span className="pguh-drug-field__label">Description</span><textarea className="nhsuk-textarea" rows="5" value={labelForm.description} onChange={(event) => setLabelForm((form) => ({ ...form, description: event.target.value }))} placeholder="Optional explanation of this administration label" /></label>
              <div className="pguh-drug-form-footer">
                <label className="pguh-drug-inline-check"><input type="checkbox" checked={labelForm.active} onChange={(event) => setLabelForm((form) => ({ ...form, active: event.target.checked }))} /><span>Active label</span></label>
                <button className="nhsuk-button pguh-drug-primary-action" type="submit" disabled={busy}>{busy ? "Saving…" : labelForm.id ? "Save changes" : "Create label"}</button>
              </div>
            </form>
          </section>

          <section className="pguh-drug-admin-panel pguh-drug-admin-register">
            <div className="pguh-drug-admin-panel__heading"><div><span className="pguh-eyebrow">Labels</span><h2>Administration labels</h2></div><strong>{matrix.labels.length}</strong></div>
            <div className="pguh-drug-admin-list">
              {matrix.labels.map((label) => <article key={label.id} className={!label.active ? "is-archived" : ""}>
                <div className="pguh-drug-admin-list__content"><h3>{label.name}</h3><p>{label.description || "No description."}</p><span className={`pguh-drug-status ${label.active ? "is-active" : "is-inactive"}`}>{label.active ? "Active" : "Inactive"}</span></div>
                <div className="pguh-drug-admin-list__actions"><button className="nhsuk-button nhsuk-button--secondary" type="button" onClick={() => editLabel(label)}><FaEdit /> Edit</button></div>
              </article>)}
            </div>
          </section>
        </div>}

        {tab === "audit" && <section className="pguh-drug-admin-panel pguh-drug-audit-panel">
          <div className="pguh-drug-admin-panel__heading"><div><span className="pguh-eyebrow">History</span><h2>Recent Drug Matrix changes</h2></div><strong>{audit.length}</strong></div>
          <div className="pguh-drug-audit">{audit.map((row) => { const drug = matrix.drugs.find((item) => item.id === row.drug_id); return <article key={row.id}><div><strong>{row.action.replaceAll("_", " ")}</strong><span>{drug?.name || "Drug record"}</span></div><div><span>{actorNames[row.changed_by] || "System / unknown"}</span><time>{new Date(row.changed_at).toLocaleString("en-GB")}</time></div></article>; })}</div>
        </section>}
      </div>
    </main>
    <Footer />
  </>;
}
